/**
 * Pure helpers for editing the JSON value backing `JsonTreeView` (Slice 15).
 *
 * Paths are dot-separated, prefixed by `root` (matching the testid contract
 * — see `jsonTreeNavigation.ts`). Object keys with literal dots are not
 * supported — same limitation as the read-only walker.
 *
 * Every mutation returns a fresh deep-cloned tree via `JSON.parse(JSON.stringify(...))`
 * so React equality checks fire and we never alias the caller's input. JSON
 * values clone losslessly under that round-trip.
 */

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

/**
 * A child's path: the parent path, a dot, and the key with `%` and `.` escaped, so a property named `a.b` stays ONE
 * segment (editing `{"a.b": 1}` must not reach a nested `a.b`). Every path builder goes through here.
 */
export function jsonTreeChildPath(path: string, key: string | number): string {
  return `${path}.${String(key).replace(/%/g, '%25').replace(/\./g, '%2E')}`;
}

/**
 * Split a path into segments, returning the root tail (segments after `root`), unescaped.
 * `'root'` → `[]`, `'root.a'` → `['a']`, `'root.0.id'` → `['0', 'id']`, `'root.a%2Eb'` → `['a.b']`.
 */
function splitPath(path: string): string[] {
  if (path === 'root') return [];
  if (!path.startsWith('root.')) {
    throw new Error(`Invalid JSON tree path: ${path}`);
  }
  return path.slice('root.'.length).split('.').map((segment) => segment.replace(/%2E/g, '.').replace(/%25/g, '%'));
}

/**
 * Resolve the parent container of a path along with the terminal key.
 * Returns `null` when the path refers to the root itself (no parent).
 */
function resolveParent(
  root: unknown,
  path: string,
): { parent: Record<string, unknown> | unknown[]; key: string | number } | null {
  const segs = splitPath(path);
  if (segs.length === 0) return null;
  let cur: unknown = root;
  for (let i = 0; i < segs.length - 1; i++) {
    cur = stepInto(cur, segs[i]);
  }
  const tailSeg = segs[segs.length - 1];
  if (Array.isArray(cur)) {
    return { parent: cur, key: Number(tailSeg) };
  }
  if (cur !== null && typeof cur === 'object') {
    return { parent: cur as Record<string, unknown>, key: tailSeg };
  }
  throw new Error(`Cannot resolve parent for path ${path}: parent is not a container`);
}

function stepInto(cur: unknown, seg: string): unknown {
  if (Array.isArray(cur)) return cur[Number(seg)];
  if (cur !== null && typeof cur === 'object') return (cur as Record<string, unknown>)[seg];
  throw new Error(`Cannot descend into non-container at segment "${seg}"`);
}

/**
 * Resolve the container at `path` (must be an object or array — caller
 * guarantees this for `+ key` / `+ element` actions). Returns the container
 * directly so the caller can mutate the cloned tree in place.
 */
function resolveContainer(root: unknown, path: string): Record<string, unknown> | unknown[] {
  const segs = splitPath(path);
  let cur: unknown = root;
  for (const seg of segs) {
    cur = stepInto(cur, seg);
  }
  if (Array.isArray(cur)) return cur;
  if (cur !== null && typeof cur === 'object') return cur as Record<string, unknown>;
  throw new Error(`Path ${path} does not point at a container`);
}

/** Replace the leaf at `path` with `newValue`. */
export function setLeafAtPath(root: unknown, path: string, newValue: JsonValue): unknown {
  const next = clone(root);
  const resolved = resolveParent(next, path);
  if (resolved === null) {
    return newValue;
  }
  const { parent, key } = resolved;
  if (Array.isArray(parent)) {
    parent[key as number] = newValue as never;
  } else {
    parent[key as string] = newValue;
  }
  return next;
}

/** Remove the entry at `path` (object key or array index). */
export function removeAtPath(root: unknown, path: string): unknown {
  const next = clone(root);
  const resolved = resolveParent(next, path);
  if (resolved === null) {
    throw new Error('Cannot remove root');
  }
  const { parent, key } = resolved;
  if (Array.isArray(parent)) {
    parent.splice(key as number, 1);
  } else {
    delete parent[key as string];
  }
  return next;
}

/**
 * Add `key: value` to the object container at `parentPath`. If `key` already
 * exists, the existing value is replaced — matching JS object semantics.
 */
export function addObjectKey(
  root: unknown,
  parentPath: string,
  key: string,
  value: JsonValue = '',
): unknown {
  const next = clone(root);
  const container = resolveContainer(next, parentPath);
  if (Array.isArray(container)) {
    throw new Error(`Cannot add object key to array at ${parentPath}`);
  }
  container[key] = value;
  return next;
}

/** Append a new element to the array container at `parentPath`. */
export function addArrayElement(
  root: unknown,
  parentPath: string,
  value: JsonValue = '',
): unknown {
  const next = clone(root);
  const container = resolveContainer(next, parentPath);
  if (!Array.isArray(container)) {
    throw new Error(`Cannot push element to non-array at ${parentPath}`);
  }
  container.push(value);
  return next;
}

/**
 * Categorise the kind of change made by an edit operation so the analytics
 * event can report it without leaking values or paths.
 */
export type JsonEditChangeKind =
  | 'leaf'
  | 'add_key'
  | 'remove_key'
  | 'add_element'
  | 'remove_element';

/**
 * A decimal number as sign + significant digits + exponent ("1.50" and "15e-1" → "15e-1"); undefined if not one. Takes
 * the spellings typed into a number field too ("+1", ".5", "5."), not only JSON's.
 */
function canonicalDecimal(literal: string): string | undefined {
  const match = /^([+-])?(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(literal.trim());
  if (!match || (!match[2] && !match[3])) return undefined;
  const [, sign, whole = '', fraction = '', exponent = '0'] = match;
  let digits = `${whole}${fraction}`.replace(/^0+/, '');
  if (!digits) return '0';
  let power = Number(exponent) - fraction.length;
  const trailing = digits.length - digits.replace(/0+$/, '').length;
  digits = digits.slice(0, digits.length - trailing);
  power += trailing;
  return `${sign === '-' ? '-' : ''}${digits}e${power}`;
}

/**
 * The literal's VALUE survives a JavaScript number (`9007199254740993` does not; `12.50` and `1e3` do — only their
 * spelling changes). Tree edits refuse a number they could not store exactly.
 */
export function isExactJsonNumber(literal: string): boolean {
  const canonical = canonicalDecimal(literal);
  const value = Number(literal);
  if (!Number.isFinite(value)) return false;
  // Other spellings `Number()` reads ("0x1F", "0b101") are integers: exact while they are safe ones ("" is no number).
  if (canonical === undefined) return literal.trim() !== '' && Number.isSafeInteger(value);
  return canonicalDecimal(JSON.stringify(value)) === canonical;
}
